import { useEffect, useRef, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import type { RootState } from '../../../store/store';
import { settlePendingAssetPatches } from './assetEditSeams';
import { photosMissingCaption } from './captionSortCheck';
import { selectGridPhotosetId, useCaptionSort } from './useCaptionSort';

export type CaptionSortPromptDialog = {
  missingCount: number;
  confirm: () => void;
  cancel: () => void;
};

type PendingConfirmation = { missingCount: number; photosetId: string };

/**
 * DV-3537: Caption Sort with the missing-caption check in front of it. Every
 * entry point (Internal PP's toolbar button, the kebab item) goes through
 * `requestCaptionSort`: no uncaptioned photos sorts straight away, as
 * DV-2859 did; otherwise the warning dialog opens and the sort waits for
 * "Sort Anyway". "Go Back & Add Captions" only closes the dialog.
 *
 * Lives at the page, not in the kebab: the dialog has to outlive the
 * dropdown, which unmounts its content the moment an item is selected.
 *
 * Order of operations matters (PR #1204 review):
 *  1. Wait for in-flight asset PATCHes. Typing a caption and clicking sort
 *     blurs the editor first, which PATCHes; the server sort must not run
 *     before that write lands. A failed PATCH has rolled the slice back by
 *     the time this resolves, so the count below sees the real state.
 *  2. Count from the store as it is NOW (not the render that was clicked).
 *  3. The photoset comes from the CLICK, not from after the wait: if the
 *     grid moved to another set while the PATCHes settled, do nothing — that
 *     set was never asked about. The id is then pinned for the dialog: one
 *     left open while the user moves to another set closes, and
 *     "Sort Anyway" sorts the pinned id regardless.
 *
 * A set with fewer than two photos never warns: the server does nothing for
 * it, so a warning about where uncaptioned photos will go would be false.
 */
export function useCaptionSortPrompt() {
  const { runCaptionSort, sorting, canCaptionSort } = useCaptionSort();
  const store = useStore<RootState>();
  const photosetId = useSelector(selectGridPhotosetId);
  // State drives the disabled controls; the ref is the re-entrancy guard,
  // because a second click in the same render would still read state false.
  const [preparing, setPreparing] = useState(false);
  const preparingRef = useRef(false);
  const [pending, setPending] = useState<PendingConfirmation | null>(null);

  useEffect(() => {
    setPending(null);
  }, [photosetId]);

  const requestCaptionSort = async (): Promise<void> => {
    if (sorting || preparingRef.current || !canCaptionSort) return;
    const clickedId = selectGridPhotosetId(store.getState());
    preparingRef.current = true;
    setPreparing(true);
    try {
      await settlePendingAssetPatches();
    } finally {
      preparingRef.current = false;
      setPreparing(false);
    }
    const state = store.getState();
    const targetId = selectGridPhotosetId(state);
    if (!targetId || targetId !== clickedId) return;
    const { imageAssets } = state.imageAsset;
    const missing =
      imageAssets.length < 2 ? 0 : photosMissingCaption(imageAssets).length;
    if (missing === 0) {
      void runCaptionSort(targetId);
      return;
    }
    setPending({ missingCount: missing, photosetId: targetId });
  };

  const dialog: CaptionSortPromptDialog | null =
    pending === null
      ? null
      : {
          missingCount: pending.missingCount,
          confirm: () => {
            setPending(null);
            void runCaptionSort(pending.photosetId);
          },
          cancel: () => setPending(null)
        };

  return {
    requestCaptionSort: () => void requestCaptionSort(),
    sorting: sorting || preparing,
    canCaptionSort,
    dialog
  };
}
